import { Trophy } from 'lucide-react'

import robotImage from '../assets/robot.png'
import {
  objectiveCountLabel,
  nextMilestone,
  passProgress,
  remainingObjectivesForNextMilestone,
  tierRewardSummary
} from '../lib/pass-ui'
import type { Pass } from '../types/pass'

import { RewardStack } from './RewardVisual'

interface PassHeroProps {
  pass: Pass
}

export function PassHero({ pass }: PassHeroProps) {
  const progress = passProgress(pass)
  const displayProgress = Math.round(progress)
  const milestone = nextMilestone(pass)
  const remainingObjectives = remainingObjectivesForNextMilestone(pass)
  const remainingLead = remainingObjectives === 1 ? 'Falta' : 'Faltam'
  const remainingMessage =
    milestone && remainingObjectives > 0
      ? `${remainingLead} ${objectiveCountLabel(remainingObjectives)} para liberar o próximo baú!`
      : milestone
        ? 'Seu próximo baú já está liberado.'
        : 'Todos os baús deste passe já foram liberados.'
  const mobileMessage =
    milestone && remainingObjectives > 0
      ? `${remainingLead} ${objectiveCountLabel(remainingObjectives)} para liberar a próxima recompensa.`
      : milestone
        ? 'Sua próxima recompensa já está liberada.'
        : 'Todas as recompensas deste passe já foram liberadas.'

  return (
    <section className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,#2C0E66_0%,#1C0843_55%,#160637_100%)] px-4 py-5 shadow-[0_20px_45px_rgba(8,3,24,0.4)] sm:px-8 sm:py-7">
      <div className="absolute -right-10 top-6 size-40 rounded-full bg-[#7E31FF]/18 blur-3xl" aria-hidden />
      <div className="absolute left-10 top-10 size-3 rounded-full bg-[#6C2EFF]/80" aria-hidden />
      <div className="absolute left-[52%] top-24 size-2 rounded-full bg-[#8A58FF]/90" aria-hidden />
      <div className="absolute right-8 top-20 size-2 rounded-full bg-[#7E31FF]/70" aria-hidden />

      <div className="relative grid grid-cols-[minmax(0,1fr)_118px] items-end gap-2 sm:grid-cols-[minmax(0,1fr)_290px] sm:gap-8">
        <div className="min-w-0">
          <p className="text-[1.2rem] font-extrabold tracking-[-0.03em] text-[#B98BFF] sm:text-[2rem]">
            Seu progresso
          </p>

          <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1 leading-none">
            <span className="text-[2.7rem] font-black tracking-[-0.07em] text-[#A94BFF] min-[390px]:text-[3.2rem] sm:text-[4.8rem]">
              {displayProgress}%
            </span>
            <span className="pb-1 text-[1.15rem] font-black tracking-[-0.04em] text-white min-[390px]:text-[1.35rem] sm:text-[2.2rem]">
              concluído
            </span>
          </div>

          <div className="mt-5 h-5 w-full overflow-hidden rounded-full bg-[#19073A] shadow-[inset_0_2px_8px_rgba(0,0,0,0.35)]">
            <div
              className="h-full rounded-full bg-[linear-gradient(90deg,#B665FF_0%,#9136FF_60%,#7428FF_100%)] shadow-[0_0_12px_rgba(173,106,255,0.55)]"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p className="mt-5 pr-2 text-[0.9rem] font-semibold leading-snug tracking-[-0.02em] text-white sm:hidden">
            {mobileMessage}
          </p>

          <p className="mt-5 hidden max-w-md text-[1rem] font-semibold leading-snug tracking-[-0.02em] text-white sm:block sm:text-[1.8rem]">
            {remainingMessage}
          </p>

          <div className="mt-6 hidden max-w-md items-center gap-4 rounded-[1.5rem] bg-[linear-gradient(180deg,rgba(31,10,73,0.88)_0%,rgba(23,8,54,0.92)_100%)] p-4 sm:flex">
            <div className="shrink-0 rounded-[1.2rem] bg-[#19073A] p-2.5 sm:p-3">
              {milestone ? (
                <RewardStack
                  items={milestone.pass_reward_tier_items}
                  frameClassName="size-16 sm:size-[84px]"
                />
              ) : (
                <div className="flex size-16 items-center justify-center sm:size-[84px]">
                  <Trophy className="size-2/3 text-pass-gold" aria-hidden />
                </div>
              )}
            </div>

            <div className="min-w-0">
              <p className="text-[0.95rem] font-bold tracking-[-0.03em] text-[#B98BFF] sm:text-[1.15rem]">
                {milestone ? 'Próximo marco' : 'Passe concluído'}
              </p>
              <p className="mt-1 text-[1rem] font-black tracking-[-0.04em] text-[#FFD133] min-[390px]:text-[1.15rem] sm:text-[1.8rem]">
                {milestone ? `${milestone.percent_threshold}% concluído` : '100% concluído'}
              </p>
              <p className="mt-1 text-[0.85rem] font-semibold tracking-[-0.02em] text-[#B79BE8] sm:text-[1.15rem]">
                {milestone ? tierRewardSummary(milestone) : 'Todas as recompensas foram liberadas'}
              </p>
            </div>
          </div>
        </div>

        <div className="relative flex justify-end self-end">
          <img
            src={robotImage}
            alt="Mascote do passe"
            className="w-full max-w-[160px] object-contain drop-shadow-[0_14px_30px_rgba(0,0,0,0.35)] sm:max-w-[270px]"
          />
        </div>
      </div>
    </section>
  )
}
